/** Splits a daily-bucketed timeline in two halves and reports the % delta. */
export function halfPeriodDelta(values) {
    if (values.length < 2)
        return null;
    const half = Math.floor(values.length / 2);
    const previous = values.slice(0, half).reduce((acc, v) => acc + v, 0);
    const current = values.slice(half).reduce((acc, v) => acc + v, 0);
    if (previous === 0)
        return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
}
export function bestDay(timeline) {
    if (timeline.length === 0)
        return null;
    return timeline.reduce((best, p) => (p.count > best.count ? p : best), timeline[0]);
}
const IN_APP_BROWSERS = new Set([
    'facebook',
    'instagram',
    'tiktok',
    'twitter',
    'linkedin',
    'reddit',
    'whatsapp',
    'telegram',
    'messenger',
    'pinterest',
]);
const MOBILE_DEVICES = new Set(['mobile', 'iphone', 'android', 'tablet', 'ipad']);
function fmtPct(value) {
    if (Math.abs(value) < 0.05)
        return '0%';
    return `${value > 0 ? '+' : ''}${value.toFixed(value % 1 === 0 ? 0 : 1)}%`;
}
function pctOfTotal(value, total) {
    if (total <= 0)
        return 0;
    return (value / total) * 100;
}
function readableDate(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return iso;
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
export function computeWorkspaceInsights(input) {
    const { kpi, timeline, campaigns, devices, browsers } = input;
    const totalScans = kpi?.entries ?? 0;
    const insights = [];
    if (totalScans === 0)
        return insights;
    // 1. Period-over-period total scans delta (timeline halves)
    const delta = halfPeriodDelta(timeline.map((p) => p.count));
    if (delta !== null && timeline.length >= 4) {
        const tone = delta > 5 ? 'positive' : delta < -5 ? 'negative' : 'neutral';
        insights.push({
            key: 'scans-delta',
            tone,
            title: tone === 'positive'
                ? `Scans up ${fmtPct(delta)} vs. the first half of the window`
                : tone === 'negative'
                    ? `Scans down ${fmtPct(delta)} vs. the first half of the window`
                    : `Scans roughly flat across the window (${fmtPct(delta)})`,
            body: 'Computed by comparing the second half of the selected period to the first half — directional, not a calendar comparison.',
        });
    }
    // 2. Top campaign
    const topCampaign = [...campaigns].sort((a, b) => b.entries - a.entries)[0];
    if (topCampaign && topCampaign.entries > 0) {
        const share = pctOfTotal(topCampaign.entries, totalScans);
        if (share >= 5) {
            insights.push({
                key: 'top-campaign',
                tone: 'neutral',
                title: `${topCampaign.campaignName} drove ${Math.round(share)}% of scans`,
                body: share >= 50
                    ? 'A single campaign is carrying the period — consider diversifying entry points.'
                    : 'Lead campaign in the selected window.',
            });
        }
    }
    // 3. Best day
    const peak = bestDay(timeline);
    if (peak && peak.count > 0 && timeline.length >= 3) {
        const avg = totalScans / timeline.length;
        if (peak.count >= avg * 1.5) {
            insights.push({
                key: 'best-day',
                tone: 'positive',
                title: `Peak day: ${readableDate(peak.date)}, ${peak.count.toLocaleString()} scans`,
                body: `That's ${Math.round((peak.count / avg) * 10) / 10}× the daily average — worth checking what ran that day.`,
            });
        }
    }
    // 4. Mobile share
    const mobileScans = devices.filter((d) => MOBILE_DEVICES.has(d.deviceType)).reduce((acc, d) => acc + d.scans, 0);
    const mobileTotal = devices.reduce((acc, d) => acc + d.scans, 0);
    if (mobileTotal > 0) {
        const share = pctOfTotal(mobileScans, mobileTotal);
        if (share >= 70) {
            insights.push({
                key: 'mobile-share',
                tone: 'neutral',
                title: `${Math.round(share)}% of traffic is on mobile`,
                body: 'Make sure destinations and tracking links render well on small screens.',
            });
        }
        else if (share <= 30) {
            insights.push({
                key: 'desktop-share',
                tone: 'neutral',
                title: `${Math.round(100 - share)}% of traffic is on desktop`,
                body: 'Unusual for offline-to-online intent — likely lots of test or office scans.',
            });
        }
    }
    // 5. In-app browser standout
    const inAppEntries = browsers.filter((b) => IN_APP_BROWSERS.has(b.browserType));
    const browserTotal = browsers.reduce((acc, b) => acc + b.scans, 0);
    if (browserTotal > 0 && inAppEntries.length > 0) {
        const topInApp = inAppEntries.reduce((m, e) => (e.scans > m.scans ? e : m), inAppEntries[0]);
        const share = pctOfTotal(topInApp.scans, browserTotal);
        if (share >= 15) {
            insights.push({
                key: 'in-app-standout',
                tone: 'neutral',
                title: `${capitalize(topInApp.browserType)} in-app is ${Math.round(share)}% of browser traffic`,
                body: 'In-app webviews can block external links and tracking — consider an entry point optimized for that channel.',
            });
        }
    }
    // 6. Unique-visitor ratio (repeat-scan signal)
    if (kpi && kpi.entries > 0 && kpi.uniqueVisitors > 0) {
        const ratio = kpi.entries / kpi.uniqueVisitors;
        if (ratio >= 2) {
            insights.push({
                key: 'repeat-scans',
                tone: 'positive',
                title: `Each visitor scanned ${ratio.toFixed(1)}× on average`,
                body: 'High repeat-scan rate — visitors are coming back, content is sticky.',
            });
        }
    }
    return insights.slice(0, 5);
}
export function computeCampaignInsights(input) {
    const { kpis, timelines, devices, browsers } = input;
    const insights = [];
    if (kpis.totalScans === 0)
        return insights;
    const delta = halfPeriodDelta(timelines.map((p) => p.scans));
    if (delta !== null && timelines.length >= 4) {
        const tone = delta > 5 ? 'positive' : delta < -5 ? 'negative' : 'neutral';
        insights.push({
            key: 'scans-delta',
            tone,
            title: tone === 'positive'
                ? `Scans accelerating: ${fmtPct(delta)} in the back half`
                : tone === 'negative'
                    ? `Scans cooling: ${fmtPct(delta)} in the back half`
                    : `Scans flat across the window (${fmtPct(delta)})`,
        });
    }
    if (kpis.topLink && kpis.topLink.share >= 60) {
        insights.push({
            key: 'top-link-dominant',
            tone: 'neutral',
            title: `${kpis.topLink.label} captures ${kpis.topLink.share}% of clicks`,
            body: 'A single destination dominates — diversify if other links are underperforming.',
        });
    }
    else if (kpis.topLink && kpis.topLink.share <= 35 && kpis.topLink.share > 0) {
        insights.push({
            key: 'top-link-balanced',
            tone: 'positive',
            title: `Destinations are well-balanced — top link is only ${kpis.topLink.share}%`,
        });
    }
    const peak = bestDay(timelines.map((t) => ({ date: t.date, count: t.scans })));
    if (peak && peak.count > 0 && timelines.length >= 3) {
        const avg = kpis.totalScans / timelines.length;
        if (peak.count >= avg * 1.5) {
            insights.push({
                key: 'best-day',
                tone: 'positive',
                title: `Peak: ${readableDate(peak.date)} — ${peak.count.toLocaleString()} scans`,
                body: `${Math.round((peak.count / avg) * 10) / 10}× the daily average for this campaign.`,
            });
        }
    }
    const mobileScans = devices.filter((d) => MOBILE_DEVICES.has(d.deviceType)).reduce((acc, d) => acc + d.scans, 0);
    const deviceTotal = devices.reduce((acc, d) => acc + d.scans, 0);
    if (deviceTotal > 0) {
        const share = pctOfTotal(mobileScans, deviceTotal);
        if (share >= 80) {
            insights.push({
                key: 'mobile-heavy',
                tone: 'neutral',
                title: `${Math.round(share)}% mobile — likely a physical-world surface`,
            });
        }
    }
    const inAppEntries = browsers.filter((b) => IN_APP_BROWSERS.has(b.browserType));
    const browserTotal = browsers.reduce((acc, b) => acc + b.scans, 0);
    if (browserTotal > 0 && inAppEntries.length > 0) {
        const topInApp = inAppEntries.reduce((m, e) => (e.scans > m.scans ? e : m), inAppEntries[0]);
        const share = pctOfTotal(topInApp.scans, browserTotal);
        if (share >= 20) {
            insights.push({
                key: 'in-app-channel',
                tone: 'neutral',
                title: `${capitalize(topInApp.browserType)} in-app is ${Math.round(share)}% of traffic`,
                body: 'This campaign is being shared inside that app — make sure the destination handles in-app webview behavior.',
            });
        }
    }
    if (kpis.totalScans > 0 && kpis.uniqueVisitors > 0) {
        const ratio = kpis.totalScans / kpis.uniqueVisitors;
        if (ratio >= 2) {
            insights.push({
                key: 'repeat-rate',
                tone: 'positive',
                title: `Each visitor scanned ${ratio.toFixed(1)}× on average`,
            });
        }
    }
    return insights.slice(0, 5);
}
function capitalize(s) {
    if (!s)
        return s;
    return s.charAt(0).toUpperCase() + s.slice(1);
}
/**
 * Per-comparison insight surfacing for A/B testing. Each ResultsCard
 * gets one focused callout — not a full 5-insight panel — so the
 * overview stays scannable across multiple comparisons.
 */
export function computeABComparisonInsight(input) {
    const { itemName, totalScans, winnerLabel, winnerShare, runnerUpShare } = input;
    if (totalScans === 0)
        return null;
    if (totalScans < 20) {
        return {
            key: 'low-data',
            tone: 'neutral',
            title: `Need ${20 - totalScans} more scans for a reliable read`,
            body: `Only ${totalScans} scan${totalScans === 1 ? '' : 's'} so far across ${itemName.toLowerCase()}s — keep the test running.`,
        };
    }
    if (winnerLabel === undefined || winnerShare === undefined)
        return null;
    const lead = winnerShare - (runnerUpShare ?? 0);
    if (lead >= 15) {
        return {
            key: 'clear-winner',
            tone: 'positive',
            title: `${winnerLabel} is the clear winner (${Math.round(winnerShare)}%)`,
            body: `Leading the runner-up by ${Math.round(lead)} pts. Consider promoting this ${itemName.toLowerCase()}.`,
        };
    }
    if (lead < 5 && totalScans >= 50) {
        return {
            key: 'tight-race',
            tone: 'neutral',
            title: `Tight race — ${winnerLabel} only leads by ${Math.round(lead)} pts`,
            body: `Differences this small may be noise. Run the test longer before acting.`,
        };
    }
    return {
        key: 'leading',
        tone: 'neutral',
        title: `${winnerLabel} is leading with ${Math.round(winnerShare)}%`,
    };
}
/**
 * Theme experiment insight — one focused callout. CTR-based (not share)
 * because the routing is sticky-by-fingerprint, so every variant gets
 * a fair sample and what we care about is conversion per impression.
 */
export function computeThemeExperimentInsight(input) {
    const { totalScans, variants } = input;
    if (variants.length === 0)
        return null;
    if (totalScans < 30) {
        return {
            key: 'theme-low-data',
            tone: 'neutral',
            title: `Need ${30 - totalScans} more scans for a meaningful CTR read`,
            body: 'Theme A/B is sticky-routed by visitor fingerprint — give it a few days of traffic before declaring a winner.',
        };
    }
    const sorted = [...variants].sort((a, b) => b.clickThroughRate - a.clickThroughRate);
    const leader = sorted[0];
    const runnerUp = sorted[1];
    if (leader.scans < 15) {
        return {
            key: 'theme-leader-underexposed',
            tone: 'neutral',
            title: `Variant ${leader.key} only has ${leader.scans} scans — wait for more`,
        };
    }
    if (!runnerUp) {
        return {
            key: 'theme-single-variant',
            tone: 'neutral',
            title: `Variant ${leader.key}: ${(leader.clickThroughRate * 100).toFixed(1)}% CTR`,
            body: "Add a second variant to actually compare — a single-arm test isn't an A/B test.",
        };
    }
    const leadPts = (leader.clickThroughRate - runnerUp.clickThroughRate) * 100;
    if (leadPts >= 8) {
        return {
            key: 'theme-clear-winner',
            tone: 'positive',
            title: `Variant ${leader.key} is winning by ${leadPts.toFixed(1)} CTR pts`,
            body: `Sample size looks adequate — consider declaring ${leader.key} the winner to promote it onto the campaign.`,
        };
    }
    if (leadPts < 2) {
        return {
            key: 'theme-tight-race',
            tone: 'neutral',
            title: `Tight race — ${leader.key} only leads by ${leadPts.toFixed(1)} CTR pts`,
            body: 'Differences this small are likely noise. Keep running or add a third variant.',
        };
    }
    return {
        key: 'theme-leading',
        tone: 'neutral',
        title: `Variant ${leader.key} is leading: ${(leader.clickThroughRate * 100).toFixed(1)}% CTR`,
        body: `${leadPts.toFixed(1)} pts ahead of ${runnerUp.key} — keep an eye on it.`,
    };
}
